'use client';

import type { Market, MarketSource } from '@/libs/market/market';
import { useEffect } from 'react';
import { isCanadaStorefrontEnabled } from '@/libs/market/canadaStorefrontAvailability';
import { getMarketRedirectLocation, MARKET_SOURCE } from '@/libs/market/market';
import { useUserMeQuery } from '@/queries/user/user.queries';

type MarketRouteLocation = Pick<Location, 'pathname' | 'search' | 'hash' | 'replace'>;

export function syncMarketRoute(
  market: Market,
  marketSource: MarketSource | undefined,
  location: MarketRouteLocation,
) {
  if (!marketSource || marketSource === MARKET_SOURCE.UNRESOLVED) {
    return;
  }

  if (market === 'CA' && !isCanadaStorefrontEnabled()) {
    return;
  }

  const targetLocation = getMarketRedirectLocation(
    location.pathname,
    location.search,
    location.hash,
    market,
  );

  if (targetLocation) {
    location.replace(targetLocation);
  }
}

/**
 * Corrects the route after login, access-token refresh, or a backend market update.
 * Backend `user.market` is authoritative only after `marketSource` is resolved.
 */
export default function MarketRouteSync() {
  const { data: user } = useUserMeQuery();

  useEffect(() => {
    if (!user?.market) {
      return;
    }

    // A hard navigation discards client data cached for the previous market.
    syncMarketRoute(user.market, user.marketSource, window.location);
  }, [user?.market, user?.marketSource]);

  return null;
}
